import { HttpContext, HttpErrorResponse, HttpEvent, HttpEventType, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, Observer, ReplaySubject, throwError } from 'rxjs';
import { catchError, delay, map, mergeMap } from 'rxjs/operators';
import { BYPASS_HTTP_QUEUE, HANDLE_ERRORS_LOCALLY, SKIP_FORBIDDEN_REDIRECT } from 'app/shared/interceptors/http-context-tokens';
import { ProjectChangeMessage } from '../models';
import { AppService, GlobalBusyService, NotificationService, Project, ProjectEditStatus, ProjectsService, ThreatReductionResult } from '../services';
import { AuthService } from '../services/auth/auth.service';
import { CaseUtils } from '../utils';


interface RequestItem {
  requestHandler: Observable<HttpEvent<any>>;
  observer: Observer<HttpEvent<any>>;
}

@Injectable()
export class MiradiHttpInterceptor implements HttpInterceptor {

  private readonly diagramRelatedApiEndpoints = [
    '/projectdiagramfactors',
    '/projectdiagramfactorvalidatedelete',
    '/projectdiagramlinks',
    '/projectdiagramtags',
    '/groupboxes',
    '/scopeboxes',
    '/textboxes',
  ];
  private readonly projectEditStatusApiEndpoint = '/projecteditstatus';

  private pendingObservers: Observer<void>[];
  private project: Project;
  private requestItemsQueue: RequestItem[] = [];
  private sessionExpiredSignalled = false;
  private signInRedirected = false;

  constructor(
    private appService: AppService,
    private authService: AuthService,
    private globalBusyService: GlobalBusyService,
    private notificationService: NotificationService,
    private projectsService: ProjectsService,
    private router: Router,
    private translateService: TranslateService,
  ) {
    this.appService.listenToProjectChangeMessage()
    .subscribe((pcm: ProjectChangeMessage) => {
      this.project = pcm?.project;
    });
  }

  // A 401 means the server session lapsed (e.g. it hit its hard lifetime cap
  // while a background poll kept an unattended tab open). Redirecting to the login
  // flow right then strands an unattended tab on the hosted login page until the
  // auth state expires and the callback is rejected. Instead raise a session-expired
  // signal so the shell can show a modal; re-auth only fires when the user clicks it,
  // guaranteeing it starts with the user present. Guarded so the many concurrent
  // calls that 401 together only raise it once.
  private signalSessionExpired() {
    // Swallowed 401s never resolve their caller, so nothing reaches the setBusy(false)
    // in its success handler. Undo it on every 401, not just the first: whatever the
    // user does next sets the spinner again, and the spinner covers the modal below.
    this.globalBusyService.setBusy(false);

    if (this.sessionExpiredSignalled) return;
    this.sessionExpiredSignalled = true;

    // Anything already on screen is a symptom of the lapsed session, not news.
    this.notificationService.clear();
    this.authService.notifySessionExpired();
  }

  private redirectAfterAccessError(path: string): Observable<never> {
    this.globalBusyService.setBusy(false);
    if (!window.location.pathname.startsWith(`/ux/${path}`)) {
      this.router.navigate([`/${path}`]);
    }
    return EMPTY;
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (
      ['POST', 'PUT', 'DELETE'].indexOf(request.method) >= 0 ||
      (request.url.indexOf(this.projectEditStatusApiEndpoint) >= 0 && request.method === 'GET' && !request.context.get(BYPASS_HTTP_QUEUE))
    ) {
      return new Observable((observer: Observer<any>) => {
        this.requestItemsQueue.push({
          requestHandler: this.handleRequest(request, next),
          observer: observer,
        });

        if (this.requestItemsQueue.length === 1) {
          this.dispatchRequest();
        }
      });
    } else {
      return this.handleRequest(request, next);
    }
  }

  private dispatchRequest(): void {
    if (this.requestItemsQueue.length <= 0) return;

    const requestItem = this.requestItemsQueue[0];
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;

      this.processNextRequest();
    };

    requestItem.requestHandler
    .subscribe({
      next: (event: HttpEvent<any>) => {
        if (!(event instanceof HttpResponse)) return;

        requestItem.observer.next(event);
        requestItem.observer.complete();
        settle();
      },
      error: (error: any) => {
        requestItem.observer.error(error);
        settle();
      },
      // EMPTY is terminal too. Complete the caller and advance instead of leaving
      // every later save behind a request whose error was deliberately swallowed.
      complete: () => {
        requestItem.observer.complete();
        settle();
      },
    });
  }

  private processNextRequest(): void {
    if (this.requestItemsQueue?.length > 0) {
      this.requestItemsQueue.shift();
    }
    this.dispatchRequest();
  }

  private handleRequest(request: HttpRequest<any>, next: HttpHandler, observer?: Observer<any>): Observable<HttpEvent<any>> {
    return next.handle(request)
    .pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          if (event.headers.has('MS-Project-Edit-Event-Datetime')) {
            this.appService.projectEditEventDatetime = event.headers.get('MS-Project-Edit-Event-Datetime');
          }

          // Check for account setup required header
          if (event.headers.get('MS-Account-Setup-Required') === 'true') {
            // Don't redirect if already on account setup pages to prevent infinite loop
            if (!window.location.pathname.startsWith('/ux/account-setup')) {
              setTimeout(() => {
                this.router.navigate(['/account-setup']);
              }, 0);
            }
          }
        }

        if (observer) {
          observer.next(event);
          observer.complete();
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 0) {
          return this.handleNetworkError()
          .pipe(
            delay(500),
            mergeMap(() => {
              // Retry inside the current handler. Re-entering intercept() would create
              // a second dispatcher for the same queue head, so both could advance it.
              return this.handleRequest(request.clone(), next);
            })
          );
        } else if (error.status === 403 && error.error?.detail?.startsWith('ACCOUNT_DISABLED')) {
          return this.redirectAfterAccessError('account-disabled');
        } else if (error.status === 403 && error.error?.detail?.startsWith('STAGE_ACCESS_DENIED')) {
          return this.redirectAfterAccessError('stage-access-denied');
        } else if (error.status === 403 && error.error?.detail?.startsWith('ACCOUNT_NOT_PROVISIONED')) {
          return this.redirectAfterAccessError('account-not-provisioned');
        } else if (request.url.endsWith('/user')) {
          // Keep throwing: getSessionUser turns this into a null user, which is how the
          // guards decide an anonymous visitor should go to discovery. But if we already
          // had a user, this 401 is a lapsed session like any other, so raise the modal
          // rather than let it bounce them through the hosted login unannounced.
          if (error.status === 401 && this.authService.currentUser) this.signalSessionExpired();

          return throwError(() => error);
        } else if (request.context.get(HANDLE_ERRORS_LOCALLY)) {
          return throwError(() => error);
        } else if (error.status === 401) {
          if (this.authService.hadSession) {
            this.signalSessionExpired();
          } else {
            // Public project routes are intentionally unguarded. After an explicit
            // logout they can issue protected API calls before any route guard runs;
            // send that anonymous visitor through normal sign-in, not expiry recovery.
            this.globalBusyService.setBusy(false);
            if (!this.signInRedirected) {
              this.signInRedirected = true;
              this.authService.discovery();
            }
          }

          // Swallow this terminal session-level error. Propagating it only lets
          // each caller add a misleading message (e.g. "import failed") while
          // the tab is already redirecting or showing the recovery dialog.
          return EMPTY;
        } else if (error.status === 403) {
          if (!request.context.get(SKIP_FORBIDDEN_REDIRECT)) {
            setTimeout(() => {
              this.router.navigate(['/home-user']);
            }, 2000);
          }

          return throwError(() => error);
        } else if (error.status === 409) {

          const errorMsg = this.translateService.instant('Your changes couldn’t be saved as the system updated this information. We’re syncing the latest data for you. Please try the change again.');
          this.notificationService.warn(
            this.translateService.instant('Update Required'),
            errorMsg,
          );
          return throwError(() => new Error(errorMsg));
        } else if (error.status === 503) {
          setTimeout(() => {
            window.location.href = window.location.href;
          }, 2000);

          return throwError(() => error);
        }

        if (error.error) {
          error = this.camelcase(error.error);
        } else {
          error = this.camelcase(error);
        }

        if (
          !this.diagramRelatedApiEndpoints.some((apiEndpoint: string) => {
            return request.url.indexOf(apiEndpoint) >= 0;
          })
        ) {
          // if we are here, there's been an error not related to any diagram calls
          this.notificationService.apiError(error);
          return throwError(() => error);
        } else if (error?.status === 400 || error?.status === 500) {
          // if we are here, there's been an error in a diagramRelatedApiEndpoint
          this.notificationService.clear();

          this.notificationService.apiError(error, 0, false);

          this.appService.emitProjectChangeMessage({ project: this.project, source: 'diagram-invalidate-undo', options: { diagramColorPaletteChanged: false, invalidateDiagramUndo: true, } }, false)

          return throwError(() => error);
        }

        return this.projectsService.getProjectEditStatus(this.project.identifier, 'body', false, {
          context: new HttpContext().set(BYPASS_HTTP_QUEUE, true),
        })
        .pipe(
          mergeMap((pes: ProjectEditStatus) => {
            if (
              this.appService.projectEditEventDatetime &&
              this.appService.projectEditEventDatetime !== pes.projectEditEventDatetime
            ) {
              if ((window as any).awsRum) {
                (window as any).awsRum.recordEvent('CustomEvent', {
                  message: 'ProjectRefreshed - After Client Error Check',
                  level: 'Information'
                });
              }

              this.notificationService.clear();

              this.notificationService.info(
                this.translateService.instant('Project has been updated'),
                this.translateService.instant('Refresh for current version'),
                0,
                { type: 'projectEditStatus' }
              );

              this.notificationService.apiError(error, 0, false);

              this.notificationService.warn(
                this.translateService.instant('Undo Stack'),
                this.translateService.instant('Due to the above error, and the fact that the project has been updated, the diagram undo stack was cleared.'),
                0
              );

              this.appService.emitProjectChangeMessage({ project: this.project, source: 'diagram-invalidate-undo', options: { diagramColorPaletteChanged: false, invalidateDiagramUndo: true, } }, false);
            } else {
              this.notificationService.apiError(error);
            }

            return throwError(() => error);
          })
        );
      })
    );
  }


  private handleNetworkError(): Observable<void> {
    this.globalBusyService.setBusy(false);
    return new Observable((observer: Observer<void>) => {
      this.pendingObservers = this.pendingObservers || [];
      this.pendingObservers.push(observer);

      if ((window as any).awsRum) {
        (window as any).awsRum.recordError(new Error('Network Error - Failed to reach the server.'));
      }

      this.appService.globalInfoDialog.show({
        title: this.translateService.instant('Network Error'),
        message: this.translateService.instant('Failed to reach the server.'),
        buttonText: this.translateService.instant('Retry'),
      });
      this.appService.globalInfoDialog.onClose = () => {
        for (const obs of this.pendingObservers || []) {
          obs.next();
          obs.complete();
        }
      };
    });
  }

  private camelcase(obj: any): any {
    if (typeof obj === 'string') {
        return obj;
    } else {
        return CaseUtils.toCamel(obj);
    }
  }
}
